import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { getUploadFileTypes } from '@/services/data-sources/get-upload-file-types';
import * as Sentry from '@sentry/react';

export type TimeUnit = 'minutes' | 'hours' | 'days';

export interface SoftDeleteTtlConfig {
  value: number;
  unit: TimeUnit;
}

interface UseGetSoftDeleteTtlOptions
  extends Omit<
    UseQueryOptions<SoftDeleteTtlConfig>,
    'queryKey' | 'queryFn' | 'staleTime' | 'retry'
  > {
  orgId?: string;
}

const ALLOWED_UNITS: TimeUnit[] = ['minutes', 'hours', 'days'];
const DEFAULT_TTL: SoftDeleteTtlConfig = { value: 7, unit: 'days' };

/**
 * Fetches the soft delete TTL (time-to-live) configuration from the backend.
 * Returns default values (7 days) if the API call fails or returns invalid data.
 *
 * @param options - Query options including orgId for cache scoping and enabled flag
 * @returns Query result with TTL configuration
 */
export const useGetSoftDeleteTtl = (options?: UseGetSoftDeleteTtlOptions) => {
  const { orgId, ...queryOptions } = options || {};

  return useQuery<SoftDeleteTtlConfig>({
    queryKey: ['soft-delete-ttl', orgId],
    queryFn: async () => {
      const response = await getUploadFileTypes();

      // Handle API error response
      if (response?.detail) {
        console.error('Failed to fetch soft delete TTL:', response.detail);
        Sentry.captureException(new Error(response.detail), {
          extra: {
            context: 'fetch-soft-delete-ttl',
            orgId,
            detail: response.detail,
          },
        });
        return DEFAULT_TTL;
      }

      const rawValue = response?.data?.soft_delete_ttl_value;
      const rawUnit = response?.data?.soft_delete_ttl_unit;

      // Validate value (must be a finite number, including 0)
      const hasValidValue =
        typeof rawValue === 'number' && Number.isFinite(rawValue);

      // Validate unit (must be one of the allowed units)
      const isValidUnit =
        typeof rawUnit === 'string' &&
        ALLOWED_UNITS.includes(rawUnit as TimeUnit);

      if (hasValidValue && isValidUnit) {
        return {
          value: rawValue,
          unit: rawUnit as TimeUnit,
        };
      }

      // Log warning to Sentry if we received invalid data
      if (rawValue != null || rawUnit != null) {
        Sentry.captureMessage(
          'Invalid soft delete TTL configuration received from backend',
          {
            level: 'warning',
            extra: {
              context: 'fetch-soft-delete-ttl',
              orgId,
              soft_delete_ttl_value: rawValue,
              soft_delete_ttl_unit: rawUnit,
            },
          },
        );
      }

      // Return defaults if data is missing or invalid
      return DEFAULT_TTL;
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: 1, // Only retry once on failure
    ...queryOptions,
  });
};
